import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * One-time WORLD26 support-hours notice, shown under the global header on
 * every page. Configured in code on purpose (no admin authoring in this
 * ticket). The window is stored as absolute instants so the check is a plain
 * comparison and cannot drift with the viewer's time zone or DST:
 *   start: 2026-09-21 00:00:00 ET (EDT, UTC-4)  -> 2026-09-21T04:00:00Z
 *   end:   2026-09-26 23:59:59 ET  (exclusive)  -> 2026-09-27T04:00:00Z
 */
export const NOTICE_START_MS = Date.UTC(2026, 8, 21, 4, 0, 0);
export const NOTICE_END_MS = Date.UTC(2026, 8, 27, 4, 0, 0);
export const NOTICE_COPY =
  "You may experience longer than normal response times during the WORLD26 conference, September 21-25. Member Success will be available M-F, 9:00 AM-6:00 PM ET.";

/** CSS variable the app chrome subtracts from viewport-height calcs. */
export const NOTICE_BANNER_HEIGHT_VAR = "--notice-banner-height";

export function isNoticeActive(nowMs: number = Date.now()): boolean {
  return nowMs >= NOTICE_START_MS && nowMs < NOTICE_END_MS;
}

interface NoticeBannerProps {
  /** Injected clock for tests; defaults to the device clock. */
  now?: number;
}

/** Next instant the banner's visibility changes, or null if the window is past. */
function nextBoundaryMs(nowMs: number): number | null {
  if (nowMs < NOTICE_START_MS) return NOTICE_START_MS;
  if (nowMs < NOTICE_END_MS) return NOTICE_END_MS;
  return null;
}

export function NoticeBanner({ now }: NoticeBannerProps) {
  // A tab left open across the window's start or end would otherwise keep the
  // mount-time answer forever — the banner never appeared, or never left.
  // Re-read the clock exactly once per boundary instead of polling.
  const [tick, setTick] = useState(() => Date.now());
  const nowMs = now ?? tick;
  const active = isNoticeActive(nowMs);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (now !== undefined) return; // injected clock: tests drive time, no timers
    const boundary = nextBoundaryMs(nowMs);
    if (boundary === null) return;
    // setTimeout saturates past ~24.8 days; re-arm in chunks until the boundary.
    const delay = Math.min(boundary - nowMs, 2_147_483_000);
    const timer = setTimeout(() => setTick(Date.now()), Math.max(delay, 0));
    return () => clearTimeout(timer);
  }, [now, nowMs]);

  // Publish the rendered height so sidebars sized as 100vh - header stay
  // inside the viewport instead of being clipped by the banner's height.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!active || !el) {
      root.style.removeProperty(NOTICE_BANNER_HEIGHT_VAR);
      return;
    }
    const apply = () => root.style.setProperty(NOTICE_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    apply();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : undefined;
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(NOTICE_BANNER_HEIGHT_VAR);
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      ref={ref}
      role="status"
      data-testid="notice-banner"
      className="w-full border-b border-amber-300 bg-amber-50 px-3 py-2 text-center text-sm leading-snug text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/50 dark:text-amber-100 sm:px-5"
    >
      {NOTICE_COPY}
    </div>
  );
}

export default NoticeBanner;
